import { GlobalComposable } from "~/classes/composable/GlobalComposable"

interface SelectionInfo {
  file?: InterfaceFile
  search?: InterfaceSearch
  location: SelectionLocation // represents where the selection was made
}

class Selection extends GlobalComposable<{
  /** A hash map of selection indexed by their entity id */
  selected: Record<string, SelectionInfo>
}> {
  override defaultState = () => ({ selected: {} })

  protected _selectedFiles = () => {
    const files = Object.values(this.state.value.selected).map(i => i.file).filter(f => !!f)
    return dictByKey(files, f => f.fileId)
  }

  selectedFiles = computed(this._selectedFiles)

  protected _selectedFile = () => {
    const files = this._selectedFiles()
    if (len(files) !== 1)
      return undefined
    return first(files)
  }

  /** Will be undefined if more than one item are selected */
  selectedFile = computed(this._selectedFile)

  protected _selectedSearches = () => {
    const searches = Object.values(this.state.value.selected).map(i => i.search).filter(f => !!f)
    return dictByKey(searches, f => f.searchId())
  }

  selectedSearches = computed(this._selectedSearches)

  protected _selectedSearch = () => {
    const searches = Object.values(this.state.value.selected).map(i => i.search).filter(s => !!s)
    if (len(searches) > 1)
      throw new Error("More than one item selected")
    if (len(searches) === 0)
      return undefined
    return searches[0]
  }

  selectedSearch = computed(this._selectedSearch)

  isSelectionEmpty = () => {
    return len(this.state.value.selected) === 0
  }

  isSelected = (fileOrSearchId: string) => {
    return !!this.state.value.selected[fileOrSearchId]
  }

  isSelectedIn = (fileOrSearchId: string, location: SelectionLocation) => {
    return this.state.value.selected[fileOrSearchId]?.location === location
  }

  /** @deprecated use `isSelectedIn("sidebar") */
  isSelectedSidebar = (fileOrSearchId: string) => this.isSelectedIn(fileOrSearchId, "sidebar")
  /** @deprecated use `isSelectedIn("collection-view") */
  isSelectedMainView = (fileOrSearchId: string) => this.isSelectedIn(fileOrSearchId, "collection-view")
  /** @deprecated use `isSelectedIn("agent") */
  isSelectedAgent = (fileOrSearchId: string) => this.isSelectedIn(fileOrSearchId, "agent")
  /** @deprecated use `isSelectedIn("waterfall") */
  isSelectedWaterfall = (fileOrSearchId: string) => this.isSelectedIn(fileOrSearchId, "waterfall")

  clearSelected = () => {
    this.state.value.selected = {}
  }

  addFileToSelection = (file: InterfaceFile, location: SelectionLocation) => {
    this.state.value.selected[file.fileId] = { file, location }
  }

  selectFile = (file: InterfaceFile, location: SelectionLocation) => {
    this.state.value.selected = { [file.fileId]: { file, location } }
  }

  selectFiles = (files: InterfaceFile[], location: SelectionLocation) => {
    this.state.value.selected = {}
    for (const file of files)
      this.state.value.selected[file.fileId] = { file, location }
  }

  deselectFile = (fileId: string) => {
    delete this.state.value.selected[fileId]
  }

  addSearchToSelection = (search: InterfaceSearch, location: SelectionLocation) => {
    this.state.value.selected[search.searchId()] = { search, location }
  }

  selectSearch = (search: InterfaceSearch, location: SelectionLocation) => {
    this.state.value.selected = { [search.searchId()]: { search, location } }
  }

  selectSearches = (searches: InterfaceSearch[], location: SelectionLocation) => {
    this.state.value.selected = {}
    for (const search of searches)
      this.state.value.selected[search.searchId()] = { search, location }
  }

  deselectSearch = (searchId: string) => {
    delete this.state.value.selected[searchId]
  }

  selectItemLeftInFolderView = () => {
    const { contentBounds, getFileInCollection, getSortedFilesInCollection, scrollToFileInCollection } = useCollectionView()
    // TODO: go to next line if item if there is nothing to right

    const file = this._selectedFile()
    if (!file)
      throw new Error("No selected file")
    const myBounds = contentBounds.value[file.fileId]
    if (!myBounds)
      throw new Error("Selection is not in content bounds dict")
    const next = min(
      Object.entries(contentBounds.value),
      ([fileId, bounds]) => {
        if (fileId === file.fileId || bounds.right > myBounds.left)
          return undefined
        const l = myBounds.left - bounds.right
        const t = mean(bounds.top, bounds.bottom) - mean(myBounds.top, myBounds.bottom)
        return (l ** 2 + t ** 2) ** 0.5
      },
    )
    let fileId
    if (next)
      fileId = next[0]
    else {
      const sortedContents = getSortedFilesInCollection()
      const index = sortedContents.findIndex(f => f.fileId === file.fileId)
      fileId = sortedContents[index - 1]?.fileId
    }
    if (!fileId) // nothing before this item
      return console.warn("No item found to left of current item")
    const nextSelection = getFileInCollection(fileId)
    if (!nextSelection)
      throw new Error("The min content bounds item was not in folder")
    this.selectFile(nextSelection, "collection-view")
    scrollToFileInCollection(fileId)
  }

  selectItemRightInFolderView = () => {
    const { contentBounds, getFileInCollection, getSortedFilesInCollection, scrollToFileInCollection } = useCollectionView()
    // TODO: go to next line if item if there is nothing to right

    const file = this._selectedFile()
    if (!file)
      throw new Error("No selected file")
    const myBounds = contentBounds.value[file.fileId]
    if (!myBounds)
      throw new Error("Selection is not in content bounds dict")
    const next = min(
      Object.entries(contentBounds.value),
      ([fileId, bounds]) => {
        if (fileId === file.fileId || bounds.left < myBounds.right)
          return undefined
        const l = bounds.left - myBounds.right
        const t = mean(bounds.top, bounds.bottom) - mean(myBounds.top, myBounds.bottom)
        return (l ** 2 + t ** 2) ** 0.5
      },
    )
    let fileId
    if (next)
      fileId = next[0]
    else {
      const sortedContents = getSortedFilesInCollection()
      const index = sortedContents.findIndex(f => f.fileId === file.fileId)
      fileId = sortedContents[index + 1]?.fileId
    }
    if (!fileId) // nothing after this item
      return console.warn("No item found to right of current item")
    const nextSelection = getFileInCollection(fileId)
    if (!nextSelection)
      throw new Error("The min content bounds item was not in folder")
    this.selectFile(nextSelection, "collection-view")
    scrollToFileInCollection(fileId)
  }

  selectItemUpInFolderView = () => {
    const { contentBounds, getFileInCollection, scrollToFileInCollection } = useCollectionView()

    // TODO: go to next line if item if there is nothing to right
    const file = this._selectedFile()
    if (!file)
      throw new Error("No selected file")
    const myBounds = contentBounds.value[file.fileId]
    if (!myBounds)
      throw new Error("Selection is not in content bounds dict")
    const next = min(
      Object.entries(contentBounds.value),
      ([fileId, bounds]) => {
        if (fileId === file.fileId || bounds.bottom > myBounds.top)
          return undefined
        const t = myBounds.top - bounds.bottom
        const l = mean(bounds.left, bounds.right) - mean(myBounds.left, myBounds.right)
        return (l ** 2 + t ** 2) ** 0.5
      },
    )
    if (!next)
      return
    const fileId = next[0]
    const nextSelection = getFileInCollection(fileId)
    if (!nextSelection)
      throw new Error("The min content bounds item was not in folder")
    this.selectFile(nextSelection, "collection-view")
    scrollToFileInCollection(fileId)
  }

  selectItemDownInFolderView = () => {
    const { contentBounds, getFileInCollection, scrollToFileInCollection } = useCollectionView()

    // TODO: go to next line if item if there is nothing to right

    const file = this._selectedFile()
    if (!file)
      throw new Error("No selected file")
    const myBounds = contentBounds.value[file.fileId]
    if (!myBounds)
      throw new Error("Selection is not in content bounds dict")
    const next = min(
      Object.entries(contentBounds.value),
      ([fileId, bounds]) => {
        if (fileId === file.fileId || bounds.top < myBounds.bottom)
          return undefined
        const t = bounds.top - myBounds.bottom
        const l = mean(bounds.left, bounds.right) - mean(myBounds.left, myBounds.right)
        return (l ** 2 + t ** 2) ** 0.5
      },
    )
    if (!next)
      return
    const fileId = next[0]
    const nextSelection = getFileInCollection(fileId)
    if (!nextSelection)
      throw new Error("The min content bounds item was not in folder")
    this.selectFile(nextSelection, "collection-view")
    scrollToFileInCollection(fileId)
  }

  selectItemUpInSidebarView = () => {
    const file = this._selectedFile()
    if (!file)
      throw new Error("No selected file")
    const { sortedSidebarFiles } = useSidebar()
    let prior: InterfaceFile | undefined
    for (const sidebarFile of sortedSidebarFiles.value) {
      if (sidebarFile.file.fileId === file.fileId)
        break
      else prior = sidebarFile.file
    }
    if (!prior)
      return console.error("No prior item to select in sidebar")
    this.selectFile(prior, "sidebar")
  }

  selectItemDownInSidebarView = () => {
    const file = this._selectedFile()
    if (!file)
      throw new Error("No selected file")
    const { sortedSidebarFiles } = useSidebar()
    let prior: InterfaceFile | undefined
    for (const sidebarFile of reversed(sortedSidebarFiles.value)) {
      if (sidebarFile.file.fileId === file.fileId)
        break
      else prior = sidebarFile.file
    }
    if (!prior)
      return console.error("No prior item to select in sidebar")
    this.selectFile(prior, "sidebar")
  }

  /**
   * Moves the selection to a given destination folder. We must be
   * careful to only move files / folders that are not parents of the destination.
   * Moreover, we must ensure that all files / folders have a path in order to be
   * able to move them.
   * @param destination the folder to move the files to
   * @deprecated don't need this, place in file system under moveFiles
   */
  moveSelectedFiles = (destination: InterfaceFile) => {
    const { moveFiles } = useFileSystem()
    if (!destination)
      throw new Error("Nonexistent destination")
    const destFileIds = destination.path()?.map(f => f.fileId) ?? []
    const files = this._selectedFiles()
    if (!len(files))
      throw new Error("Not selecting any files")
    const notAlreadyMoved: Record<string, InterfaceFile> = {}
    for (const file of Object.values(files)) {
      if (!file.inner().path)
        throw new Error("Cannot delete a file without a path")
      if (destFileIds.includes(file.fileId))
        throw new Error("Cannot move a folder into its own child")
      if (file.inferredParentId() !== destination.fileId)
        notAlreadyMoved[file.fileId] = file
    }
    if (!notAlreadyMoved.length)
      console.warn("All files already archived")
    moveFiles(notAlreadyMoved, destination)
  }
}

const selection = new Selection("selection", theme.colors.blue.hex)
export default function () {
  return selection.use()
}
